import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, Button, Menu, MenuItem } from '@mui/material';
// routes
import { PATH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MyAvatar from '../../../components-custom/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
// utils
import localizeValue from '../../../utils/localizeValue';
import { errorHandler } from '../../../utils/errorHandler';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const { translate } = useLocales();
  const navigate = useNavigate();

  const { user, logout, setBusiness } = useAuth();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);
  const [businessesOpen, setBusinessesOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
    setBusinessesOpen(null);
  };

  const handleBusinessesOpen = (event) => {
    setBusinessesOpen(event.currentTarget);
  };

  const handleBusinessesClose = () => {
    setBusinessesOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH.auth.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(errorHandler(error, translate('layout.navbar.error.logout')), { variant: 'error' });
    }
  };

  const handleBusinessChange = async (business) => {
    try {
      await setBusiness(business);
      navigate(PATH.root, { replace: true });
      setBusinessesOpen(null);
      enqueueSnackbar(
        translate('layout.navbar.error.pickBusiness.success', {
          business: localizeValue(business.name),
        }),
        {
          variant: 'success',
        }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(errorHandler(error, translate('layout.navbar.error.pickBusiness.error')), { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar id="account-menu" />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5, maxWidth: '100%' }}>
          <Typography id="userName" variant="subtitle2" noWrap>
            {localizeValue(user.name)}
          </Typography>
          {user.business && (
            <>
              {user.businesses.length === 1 ? (
                <Typography id="businessName" variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                  {localizeValue(user.business.name)}
                </Typography>
              ) : (
                <>
                  <Typography
                    id="businessName"
                    component={Button}
                    variant="body2"
                    color="inherit"
                    onClick={handleBusinessesOpen}
                    sx={{
                      p: 0,
                      color: 'text.secondary',
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    disableElevation
                  >
                    {localizeValue(user.business.name)}
                  </Typography>
                  <Menu
                    keepMounted
                    id="businesses-menu"
                    anchorEl={businessesOpen}
                    onClose={handleBusinessesClose}
                    open={Boolean(businessesOpen)}
                  >
                    {user.businesses.map((business) => (
                      <MenuItem
                        key={business.id}
                        selected={business.id === user.business.id}
                        onClick={() => handleBusinessChange(business)}
                      >
                        {localizeValue(business.name)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {!user.employee_account && (
          <>
            <Stack sx={{ p: 1 }}>
              <MenuItem id="profile" to={PATH.management.settings.profile} component={RouterLink} onClick={handleClose}>
                {translate('layout.navbar.profile')}
              </MenuItem>
              <MenuItem
                id="subscription"
                to={PATH.management.settings.subscription}
                component={RouterLink}
                onClick={handleClose}
              >
                {translate('layout.navbar.subscription')}
              </MenuItem>
            </Stack>

            <Divider sx={{ borderStyle: 'dashed' }} />
          </>
        )}

        <MenuItem id="logout" onClick={handleLogout} sx={{ m: 1 }}>
          {translate('layout.navbar.logout')}
        </MenuItem>
      </MenuPopover>
    </>
  );
}
